import {
  COMPONENT_RENDERING_URL,
  get_headers,
  get_token,
  verify_super_user_status
} from '@/utils/constants/constants.js';
import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';

export default {
  name: "ProbaWorkflowsSidebar",
  mixins: [alert_mixin],
  data: () => ({
    menu_items: [],
    headers: {},
    token: "",
    is_sidebar_hidden: false,
    is_loading: false,
    role: sessionStorage.getItem('user_role') || null,
    project_id: sessionStorage.getItem('project_id') || null,
    is_super_user: false,

  }),
  mounted() {
    this.headers = get_headers();
    this.token = get_token();
    this.fetch_sidebar();
    this.check_super_user();
  },
  computed: {
    is_lead() {
      // Get the stored value (will be "true", "false", or null if not set)
      const is_lead_flag = sessionStorage.getItem('is_lead');
      // If the flag exists, convert to boolean
      if (is_lead_flag !== null) {
        return is_lead_flag === 'true';
      }
      // Fallback - check lead_id directly if flag doesn't exist
      const lead_id = sessionStorage.getItem('lead_id');
      return lead_id !== null && lead_id !== 'null';
    },

    should_hide_logout() {
      // List of paths where the logout button should be hidden
      const hiddenPaths = ['/view_user_trust_form', '/view_all_user_forms'];
      return hiddenPaths.includes(this.$route.path);
    },

    should_show_template() {
      const allowed_routes = [
        '/user_management',
        '/roles_management',
        '/permission_management',
        '/action_management',
        '/assigning_permissions',
        '/creating_a_policy',
        '/manage_entity_type',
        '/manage_questions',
        '/view_template',
        '/signing_workflow',
        '/form_management',
        '/status_management',
        '/data_points',
        '/manage_risks',
        '/create_enquiry_stages',
        '/view_entity_type',
        '/create_entity',
        '/configure_template',
        '/manage_assigned_workflows',
        '/upload_template',
        '/risk_questions',
        '/create_enquiry_form',
        '/assign_questions_to_enquiry_form',
        '/services',
        '/package_services',
        '/verification_services_configurations'
      ];
      return allowed_routes.includes(this.$route.path);
    },

    sorted_menu_items() {
      const safeComponents = (this.menu_items || []).map(component => {
        // Create safe component copy using Object.assign
        const safeComponent = Object.assign({}, component || {});

        // Process sub-components
        safeComponent.sub_components = (component.sub_components || [])
          .slice()
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map(sub => {
            // Create safe sub-component copy
            const safeSub = Object.assign({}, sub || {});

            // Process child components
            safeSub.child_components = (sub.child_components || [])
              .slice()
              .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

            return safeSub;
          });

        return safeComponent;
      });

      return safeComponents;
    }
  },
  methods: {

    parse_item_url(url) {
      const origin = window.location?.origin ?? "/";
      return !url
        ? { path: "/", query: {} }
        : (({ pathname, searchParams }) => ({
            path: pathname,
            query: Object.fromEntries(searchParams),
          }))(new URL(url, origin));
    },


    get_entity_type_id(item, parent, { child_only = false } = {}) {
      // Prefer common key names on the item (child). Do NOT fall back to parent when child_only=true.
      const childId =
        item?.entity_type_id ??
        item?.entity_type ??
        item?.entityTypeId ??
        item?.entityType ??
        null;

      if (child_only) return childId;

      const parentId =
        parent?.entity_type_id ??
        parent?.entity_type ??
        parent?.entityTypeId ??
        parent?.entityType ??
        null;

      return childId ?? parentId ?? null;
    },


    get_status_from_item(item) {
      // status id from backend (FormStatus.id)
      if (item?.status !== undefined && item?.status !== null) {
        return String(item.status);
      }

      // fallback: get ?status=value from URL
      const base = this.parse_item_url(item?.url || '');
      return base.query?.status ?? null;
    },


    build_route_location(item, parent, child_only = false) {
      const base = this.parse_item_url(item?.url || '/');
      const route = { ...base, query: { ...base.query } };

      // entity_type_id
      const entity_type_id = this.get_entity_type_id(item, parent, { child_only });
      if (entity_type_id) {
        route.query.entity_type_id = String(entity_type_id);
      }

      // status ID (from backend)
      const status = this.get_status_from_item(item);
      if (status) {
        route.query.status_id = String(status);
      }

      return route;
    },




    is_item_active(item, parent) {
      const built = this.build_route_location(item, parent);
      const same_path = this.$route.path === built.path;

      const tStatus = (built.query.status || '').toLowerCase();
      const cStatus = (this.$route.query.status || '').toLowerCase();
      const status_ok = tStatus ? tStatus === cStatus : true;

      const tEntity = (built.query.entity_type_id || '').toString();
      const cEntity = (this.$route.query.entity_type_id || '').toString();
      const entity_ok = tEntity ? tEntity === cEntity : true;

      return same_path && status_ok && entity_ok;
    },

    fetch_sidebar() {
      return this.show_loader("Loading menu...")
        .then(loader => {
          this.is_loading = true;
          const params = {
            role: this.role,
            project_id: this.project_id,
          };

          return axios.get(COMPONENT_RENDERING_URL, {
            headers: this.headers,
            params
          })
            .then(response => {
              this.menu_items = response.data.data;
              return this.close_loader(loader);

            })
            .catch(error => {
              console.warn("Error fetching sidebar items:", error)
              return this.close_loader(loader);
            })
            .finally(() => {
              this.is_loading = false;
            });
        });
    },

    is_status_active(url) {
      if (!/^https?:\/\/|^\//.test(url)) return false;

      const new_url = new URL(url, window.location.origin);
      const target = (new_url.searchParams.get('status') || '').trim().toLowerCase();
      const current = (this.$route.query.status || '').trim().toLowerCase();

      const norm = s => s.replace(/[\s_]+/g, '-');
      return !!target && norm(target) === norm(current);
    },

    view_pending_applications(entity_type_name = null) {
      if (entity_type_name !== null) {
        sessionStorage.setItem('entity_type_name', entity_type_name);
      } else {
        sessionStorage.removeItem('entity_type_name');
      }
      // navigate to the same route…
      this.$router.push({ path: '/pending_applications' })
    },

    logout() {
      localStorage.clear();
      sessionStorage.clear();
      this.$router.push('/');
    },

    async check_super_user() {
      this.is_super_user = await verify_super_user_status();
    },

  },
  watch: {
    $route() {
      this.$forceUpdate();
    }
  }
};